import firebase from './firebase';

// Bands: a profile that several users own together, which can be credited as the artist on albums and songs.
//
//   bands/<bandId> = { name, bio, createdBy, createdAt, members: { <username>: ms joined } }
//   users/<username>/bands/<bandId> = true          (so a person's bands can be listed without a query)
//   albums/<albumId>/bands/<bandId> = true          (the band is one of the album's artists)
//   songs/<songId>/artists/<bandId> = true          (the band is credited on that song; same node as other artists)
//
// Every member is an equal owner: any member can edit the band and add people. The iOS app has no bands
// and ignores these nodes. A band id is a Firebase push id, so it can't clash with a username or with an
// artist who isn't on Stew (also a push id), which lets bands share the artist `key` space.

export const MAX_NAME = 60;
export const MAX_BIO = 500;

const root = () => firebase.database().ref();

const toBand = (id, v) => {
  const members = v.members || {};
  return {
    id,
    name: v.name || '',
    bio: v.bio || '',
    createdBy: v.createdBy || '',
    // Longest-standing member first.
    members: Object.keys(members).sort((a, b) => (members[a] || 0) - (members[b] || 0) || a.localeCompare(b)),
  };
};

// Resolves with the band, or null if it doesn't exist (e.g. it was deleted).
export const loadBand = async (bandId) => {
  const snap = await root().child('bands').child(bandId).once('value');
  return snap.exists() ? toBand(bandId, snap.val()) : null;
};

// Band names are shown on every album that credits the band, so they're cached for the session.
// Resolves with '' for a band that no longer exists. `forgetBand` drops the cache after a rename or delete.
const names = new Map();
export const getBandName = (bandId) => {
  if (!names.has(bandId)) {
    names.set(bandId, loadBand(bandId).then(b => (b ? b.name : '')).catch(() => { names.delete(bandId); return ''; }));
  }
  return names.get(bandId);
};
export const forgetBand = (bandId) => names.delete(bandId);

// The bands a user belongs to, A to Z. A stale entry (the band was deleted, or they were removed) is left out.
export const loadUserBands = async (username) => {
  const snap = await root().child('users').child(username).child('bands').once('value');
  const bands = (await Promise.all(Object.keys(snap.val() || {}).map(id => loadBand(id).catch(() => null))))
    .filter(b => b && b.members.includes(username));
  return bands.sort((a, b) => a.name.localeCompare(b.name));
};

export const createBand = async (username, rawName) => {
  const name = rawName.trim().slice(0, MAX_NAME);
  const id = root().child('bands').push().key;
  await root().update({
    [`bands/${id}`]: { name, createdBy: username, createdAt: Date.now(), members: { [username]: Date.now() } },
    [`users/${username}/bands/${id}`]: true,
  });
  return id;
};

export const saveBand = async (bandId, { name, bio }) => {
  await root().child('bands').child(bandId).update({ name: name.trim().slice(0, MAX_NAME), bio: bio.trim().slice(0, MAX_BIO) || null });
  forgetBand(bandId);
};

export const addMember = (bandId, username) => root().update({
  [`bands/${bandId}/members/${username}`]: Date.now(),
  [`users/${username}/bands/${bandId}`]: true,
});

export const removeMember = (bandId, username) => root().update({
  [`bands/${bandId}/members/${username}`]: null,
  [`users/${username}/bands/${bandId}`]: null,
});

// Removes the band and each member's link to it. Albums that credited it just stop showing it.
export const deleteBand = async (band) => {
  const updates = { [`bands/${band.id}`]: null };
  band.members.forEach((u) => { updates[`users/${u}/bands/${band.id}`] = null; });
  await root().update(updates);
  forgetBand(band.id);
};

// Every user, for the "add a member" search. Loaded once per session.
let directory = null;
export const loadDirectory = () => {
  if (!directory) {
    directory = root().child('users').once('value').then((snap) => {
      const users = [];
      snap.forEach((child) => { users.push({ username: child.key, name: child.child('name').val() || '' }); });
      return users;
    }).catch((e) => { directory = null; throw e; });
  }
  return directory;
};

export const getUserName = (username) => root().child('users').child(username).child('name').once('value')
  .then(snap => snap.val() || '').catch(() => '');
